import { Avatar, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Image, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { colorByProductStatus, ProductStore, translateProductStoreStatus } from '@/model';
import { formatCurrency } from '@/utils';
import { useTranslation } from 'react-i18next';

export interface ProductStoreTableProps {
  data: ProductStore[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  OnEdit: (item: ProductStore) => void;
  OnDelete: (item: ProductStore) => void;
}

export const ProductStoreTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  OnEdit,
  OnDelete,
}: ProductStoreTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<ProductStore>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_store.components.product_store_table.product_store_table_creation_date'),
    }),

    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_store.components.product_store_table.product_store_table_team'),
    }),

    columnHelper.accessor('title', {
      cell: props => (
        <Flex alignItems='center' gap={4}>
          <Avatar size='md' src={props.row.original.image_url} name={props.getValue()} bg='brand.50' color='white' />
          <Text>{props.getValue()}</Text>
        </Flex>
      ),
      header: t('pages.application_store.components.product_store_table.product_store_table_product'),
    }),
    columnHelper.accessor('partner_name', {
      cell: props => props.getValue(),
      header: t('pages.application_store.components.product_store_table.product_store_table_partner'),
    }),
    columnHelper.accessor('price', {
      cell: props => formatCurrency(Number.isNaN(props.getValue()) ? 0 : props.getValue()),
      header: t('pages.application_store.components.product_store_table.product_store_table_price'),
    }),
    columnHelper.accessor('status', {
      cell: props => (
        <Text
          position='relative'
          paddingLeft='1rem'
          color={colorByProductStatus[props.getValue()]}
          _before={{
            content: '""',
            display: 'flex',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            position: 'absolute',
            top: '50%',
            left: 0,
            transform: 'translateY(-50%)',
            backgroundColor: colorByProductStatus[props.getValue()],
          }}
        >
          {translateProductStoreStatus(props.getValue())}
        </Text>
      ),
      header: t('pages.application_store.components.product_store_table.product_store_table_status'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('pages.application_store.components.product_store_table.product_store_table_actions'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label='Mostrar mais ações'
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => OnEdit?.(props.row.original as ProductStore)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_store.components.product_store_table.product_store_table_view_details')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => OnDelete?.(props.row.original as ProductStore)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_store.components.product_store_table.product_store_table_delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
